import type { PlaybackConfig } from 'chromecast-caf-receiver/cast.framework';
import { Config } from '../config';

/**
 * Playback configuration — global Shaka tuning applied to every load.
 *
 * Default: caps the playback resolution to Full HD (1080p) on all devices via
 * {@link applyResolutionCap} — any variant above `MAX_VIDEO_WIDTH`×
 * `MAX_VIDEO_HEIGHT` is marked unplayable through Shaka `restrictions` and is
 * never selected by ABR.
 *
 * Experiment path: when `Config.EXPERIMENT_START_LOW` is enabled,
 * {@link applyStartLowNoCap} is used instead — no cap, but ABR starts low. See
 * `App.buildPlaybackConfig` for the selection.
 */

/**
 * Applies the global resolution cap to a `PlaybackConfig` via Shaka
 * `restrictions`, preserving any pre-existing `shakaConfig` keys.
 *
 * Mutates and returns the given config so it can be passed straight to
 * `context.start({ playbackConfig })` or returned from a media playback
 * info handler.
 */
export function applyResolutionCap(playbackConfig: PlaybackConfig): PlaybackConfig {
  const existing = (playbackConfig.shakaConfig ?? {}) as Record<string, unknown>;

  playbackConfig.shakaConfig = {
    ...existing,
    restrictions: {
      ...(existing.restrictions as Record<string, unknown> | undefined),
      maxWidth: Config.MAX_VIDEO_WIDTH,
      maxHeight: Config.MAX_VIDEO_HEIGHT,
    },
  };

  return playbackConfig;
}

/**
 * P1 Fase 2 experiment (`EXPERIMENT_START_LOW`, ID-1715): start playback at a
 * low rendition and let ABR climb only as far as the device sustains — **with
 * no resolution cap** (ABR may reach up to 4K where safe).
 *
 * Unlike {@link applyResolutionCap}, this sets **no** `shakaConfig.restrictions`
 * and instead lowers `initialBandwidth` (bit/s) so ABR selects the lowest
 * variant first, guaranteeing startup. Any pre-existing `shakaConfig` is left
 * untouched. Mutates and returns the given config.
 *
 * Tests the hypothesis that the shaka 3014 (MEDIA_SOURCE_OPERATION_FAILED)
 * failures came from picking a 4K variant the device can't sustain — see the
 * ID-1715 ticket for the experiment protocol and decision criteria.
 */
export function applyStartLowNoCap(playbackConfig: PlaybackConfig): PlaybackConfig {
  playbackConfig.initialBandwidth = Config.LOW_START_BANDWIDTH_BPS;
  return playbackConfig;
}
